import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../api/axios';
import endpoints from '../../api/endpoints';

export interface User {
  lastName: string;
  firstName: string;
  nuid: string;
  location: string;
}

const mockUsers: User[] = [
  { lastName: 'Smith', firstName: 'John', nuid: 'K238074', location: 'A' },
  { lastName: 'Doe', firstName: 'Jane', nuid: 'K238075', location: 'B' },
  { lastName: 'Brown', firstName: 'Charlie', nuid: 'K238076', location: 'C' },
  { lastName: 'Johnson', firstName: 'Emily', nuid: 'K238077', location: 'D' },
  { lastName: 'Williams', firstName: 'Michael', nuid: 'K238078', location: 'E' },
  { lastName: 'Jones', firstName: 'Sarah', nuid: 'K238079', location: 'F' },
  { lastName: 'Garcia', firstName: 'David', nuid: 'K238080', location: 'G' },
  { lastName: 'Martinez', firstName: 'Laura', nuid: 'K238081', location: 'H' },
  { lastName: 'Davis', firstName: 'Robert', nuid: 'K238082', location: 'I' },
  { lastName: 'Rodriguez', firstName: 'Maria', nuid: 'K238083', location: 'J' },
  { lastName: 'Lee', firstName: 'James', nuid: 'K238084', location: 'K' },
  { lastName: 'Walker', firstName: 'Linda', nuid: 'K238085', location: 'L' },
  { lastName: 'Hall', firstName: 'Kevin', nuid: 'K238086', location: 'M' },
  { lastName: 'Allen', firstName: 'Barbara', nuid: 'K238087', location: 'N' },
  { lastName: 'Young', firstName: 'Steven', nuid: 'K238088', location: 'O' },
];

interface SearchUserState {
  users: User[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  perPage: number;
}

const initialState: SearchUserState = {
  users: mockUsers,
  loading: false,
  error: null,
  currentPage: 1,
  perPage: 10,
};

export const fetchSearchUsers = createAsyncThunk(
  'searchUser/fetchSearchUsers',
  async (
    { page, perPage }: { page: number; perPage: number },
    { rejectWithValue, signal }
  ) => {
    try {
      const options: any = {
        signal,
        params: {
          page,
          per_page: perPage,
        },
      };
      // Replace with correct endpoint if available
      const response = await api.get(endpoints.ubt.myTeams.myTeamsList, options);
      return response.data;
    } catch (error: any) {
      if (
        error?.message === 'canceled' ||
        error?.message === 'Request cancelled' ||
        error?.code === 'ECONNABORTED'
      ) {
        return rejectWithValue('Request cancelled');
      }
      const errorMessage =
        error?.response?.data?.message ??
        error?.response?.data?.error ??
        error?.response?.data?.detail ??
        error?.response?.statusText ??
        error?.message ??
        'Failed to fetch search users';
      return rejectWithValue(errorMessage);
    }
  }
);

const searchUserSlice = createSlice({
  name: 'searchUser',
  initialState,
  reducers: {
    setPage(state, action: PayloadAction<number>) {
      state.currentPage = action.payload;
    },
    setUsers(state, action: PayloadAction<User[]>) {
      state.users = action.payload;
    },
    resetSearchUserState: (state) => {
      state.users = [];
      state.loading = false;
      state.error = null;
      state.currentPage = 1;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchSearchUsers.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchSearchUsers.fulfilled, (state, action) => {
      state.loading = false;
      const payload: any = action.payload;
      if (payload?.meta && payload?.items && Array.isArray(payload.items)) {
        state.users = payload.items;
      } else {
        state.users = payload ? [payload] : [];
      }
    });
    builder.addCase(fetchSearchUsers.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { setPage, setUsers, resetSearchUserState } = searchUserSlice.actions;
export default searchUserSlice.reducer;
